import React, { useEffect, Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, Outlet, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import HomeLayout  from './layout/HomeLayout';
import CookieBanner from "./components/CookieBanner";
import { initGA, isAnalyticsAllowed, trackPageView } from "./analytics";

const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const EvalsPage = lazy(() => import('./pages/EvalsPage'));
const Thanks = lazy(() => import('./pages/Thanks'));
const Privacy = lazy(() => import('./pages/Privacy'));
const NotFound = lazy(() => import('./pages/NotFound'));

const SITE_URL = 'https://simplyfai.io';
const DEFAULT_SOCIAL_IMAGE = `${SITE_URL}/og-simplyfai-1200x630.png`;
const EVALS_SOCIAL_IMAGE = `${SITE_URL}/og-simplyfai-evals-1200x630.png`;

const routeSeo = {
  '/': {
    title: 'Simplyfai | Agentic AI for Businesses',
    description:
      'Simplyfai designs and evaluates agentic AI systems around business data, tools, people, and controls.',
  },
  '/about': {
    title: 'About Simplyfai | Agentic AI for Business',
    description:
      'Learn why Simplyfai helps businesses make better AI decisions, reduce unnecessary complexity, and build from South Africa with a global standard.',
  },
  '/contact': {
    title: 'Contact Simplyfai | Agentic AI Solutions',
    description:
      'Contact Simplyfai to discuss agentic AI solutions, implementation, data foundations, operational controls, or model evaluation.',
  },
  '/evals': {
    title: 'Agentic AI Evaluations & Model Evals | Simplyfai',
    description:
      'Simplyfai Evals compare frontier models across operational execution, strategic reasoning, cost, speed, privacy, and deployment fit.',
    image: EVALS_SOCIAL_IMAGE,
  },
  '/privacy': {
    title: 'Privacy Policy | Simplyfai',
    description:
      "Read Simplyfai's privacy policy covering personal information, website analytics, cookies, security, and contact details.",
  },
  '/thanks': {
    title: 'Thank You | Simplyfai',
    description: 'Thank you for contacting Simplyfai.',
    noindex: true,
  },
};

const normalizePathname = (pathname) => {
  if (!pathname || pathname === '/') {
    return '/';
  }

  return pathname.replace(/\/+$/, '');
};

const canonicalUrlFor = (pathname) => {
  if (pathname === '/') {
    return `${SITE_URL}/`;
  }

  return `${SITE_URL}${pathname}/`;
};

const setHeadAttribute = (selector, attribute, value) => {
  const element = document.querySelector(selector);
  if (element) {
    element.setAttribute(attribute, value);
  }
};

const ensureMeta = (name) => {
  let element = document.querySelector(`meta[name="${name}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute('name', name);
    document.head.appendChild(element);
  }
  return element;
};

const SeoManager = () => {
  const location = useLocation();

  useEffect(() => {
    const pathname = normalizePathname(location.pathname);
    const seo = routeSeo[pathname] || {
      title: 'Page Not Found | Simplyfai',
      description: 'The page you are looking for could not be found.',
      noindex: true,
    };
    const canonical = canonicalUrlFor(pathname);
    const image = seo.image || DEFAULT_SOCIAL_IMAGE;

    document.title = seo.title;
    setHeadAttribute('meta[name="description"]', 'content', seo.description);
    setHeadAttribute('link[rel="canonical"]', 'href', canonical);
    setHeadAttribute('meta[property="og:title"]', 'content', seo.title);
    setHeadAttribute('meta[property="og:description"]', 'content', seo.description);
    setHeadAttribute('meta[property="og:url"]', 'content', canonical);
    setHeadAttribute('meta[property="og:image"]', 'content', image);
    setHeadAttribute('meta[name="twitter:title"]', 'content', seo.title);
    setHeadAttribute('meta[name="twitter:description"]', 'content', seo.description);
    setHeadAttribute('meta[name="twitter:image"]', 'content', image);

    const robots = document.querySelector('meta[name="robots"]');
    if (seo.noindex) {
      ensureMeta('robots').setAttribute('content', 'noindex,nofollow');
    } else if (robots) {
      robots.remove();
    }
  }, [location.pathname]);

  return null;
};

const DefaultLayout = () => {
  const location = useLocation();
  const pathname = normalizePathname(location.pathname);
  const isCompactFooter =
    pathname === '/about' ||
    pathname === '/contact' ||
    pathname === '/privacy' ||
    pathname === '/evals';
  const isEvalsRoute = pathname === '/evals';

  useEffect(() => {
    resetAllScrollPositions();
  }, [location.pathname]);

  return (
    <div className="flex flex-col">
      <header role="banner">
        <Navbar />
      </header>

      <main className={isEvalsRoute ? "flex-none md:flex-1" : "flex-1"}>
        <Outlet />
      </main>

      <Footer compact={isCompactFooter} />
    </div>
  );
};
// Global scroll reset utility
const resetAllScrollPositions = () => {
  // Reset main window scroll
  window.scrollTo({ top: 0, behavior: 'auto' });

  // Reset document and body scroll
  if (document.documentElement.scrollTop > 0) {
    document.documentElement.scrollTop = 0;
  }
  if (document.body.scrollTop > 0) {
    document.body.scrollTop = 0;
  }

  // Reset scroll container (home page desktop)
  const scrollContainer = document.querySelector('.scroll-container');
  if (scrollContainer) {
    scrollContainer.scrollTo({ top: 0, behavior: 'auto' });
  }

  // Reset any other scroll containers
  const allScrollContainers = document.querySelectorAll('[class*="overflow-y-auto"], [class*="overflow-auto"]');
  allScrollContainers.forEach(container => {
    if (container.scrollTop > 0) {
      container.scrollTo({ top: 0, behavior: 'auto' });
    }
  });
};

// Global scroll management component
const ScrollManager = () => {
  const location = useLocation();

  useEffect(() => {
    // Reset scroll on every route change
    resetAllScrollPositions();
  }, [location.pathname]);

  return null;
};

const AnalyticsTracker = () => {
  const location = useLocation();

  useEffect(() => {
    if (!isAnalyticsAllowed()) return;
    initGA();
    trackPageView(
      location.pathname + location.search + location.hash
    );
  }, [location]);

  return null;
};

const App = () => {
  return (
    <Router>
      <ScrollManager />
      <SeoManager />
      <AnalyticsTracker />
      <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<HomeLayout />} />

          <Route element={<DefaultLayout />}>
            <Route path="about"     element={<About    />} />
            <Route path="contact"   element={<Contact  />} />
            <Route path="evals"     element={<EvalsPage />} />
            <Route path="thanks"    element={<Thanks   />} />
            <Route path="privacy"   element={<Privacy  />} />
            <Route path="*"          element={<NotFound />} />
          </Route>
        </Routes>
      </Suspense>
      <CookieBanner />
    </Router>
  );
};

export default App;
